import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Divider } from 'primereact/divider';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProfileInfo = () => {
    const { user, updateProfile } = useAuth();
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        birthDate: user?.birthDate ? new Date(user.birthDate) : null,
        gender: user?.gender || null,
        address: user?.address || '',
        city: user?.city || '',
        country: user?.country || 'Perú'
    });

    const genderOptions = [
        { label: 'Masculino', value: 'male' },
        { label: 'Femenino', value: 'female' },
        { label: 'Otro', value: 'other' },
        { label: 'Prefiero no decir', value: 'not_specified' }
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            await updateProfile(formData);
            setEditing(false);
            toast.success('Perfil actualizado correctamente');
        } catch (error) {
            console.error('Error actualizando perfil:', error);
            toast.error('Error al actualizar el perfil');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
            phone: user?.phone || '',
            birthDate: user?.birthDate ? new Date(user.birthDate) : null,
            gender: user?.gender || null,
            address: user?.address || '',
            city: user?.city || '',
            country: user?.country || 'Perú'
        });
        setEditing(false);
    };

    const getInitials = () => {
        const firstName = formData.firstName || '';
        const lastName = formData.lastName || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    return (
        <div className="profile-info">
            <Card>
                <div className="flex align-items-center justify-content-between mb-4">
                    <h2 className="text-2xl font-bold text-900 m-0">
                        Información Personal
                    </h2>
                    {!editing ? (
                        <Button
                            label="Editar"
                            icon="pi pi-pencil"
                            className="p-button-outlined"
                            onClick={() => setEditing(true)}
                        />
                    ) : (
                        <div className="flex gap-2">
                            <Button
                                label="Cancelar"
                                icon="pi pi-times"
                                className="p-button-outlined p-button-secondary"
                                onClick={handleCancel}
                            />
                            <Button
                                label="Guardar"
                                icon="pi pi-check"
                                loading={loading}
                                onClick={handleSave}
                            />
                        </div>
                    )}
                </div>

                <div className="grid">
                    {/* Avatar y información básica */}
                    <div className="col-12 md:col-4">
                        <div className="text-center mb-4">
                            <Avatar
                                label={getInitials()}
                                size="xlarge"
                                shape="circle"
                                className="mb-3"
                                style={{ backgroundColor: '#6366f1', color: 'white' }}
                            />
                            <h3 className="text-xl font-semibold text-900 mb-1">
                                {formData.firstName} {formData.lastName}
                            </h3>
                            <p className="text-600 m-0">{formData.email}</p>
                        </div>
                    </div>

                    {/* Formulario de información */}
                    <div className="col-12 md:col-8">
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Nombre
                                    </label>
                                    <InputText
                                        value={formData.firstName}
                                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                                        disabled={!editing}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Apellido
                                    </label>
                                    <InputText
                                        value={formData.lastName}
                                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                                        disabled={!editing}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Email
                                    </label>
                                    <InputText
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        disabled={!editing}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Teléfono
                                    </label>
                                    <InputText
                                        value={formData.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        disabled={!editing}
                                        className="w-full"
                                        placeholder="+51 999 999 999"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Fecha de Nacimiento
                                    </label>
                                    <Calendar
                                        value={formData.birthDate}
                                        onChange={(e) => handleInputChange('birthDate', e.value)}
                                        disabled={!editing}
                                        className="w-full"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Género
                                    </label>
                                    <Dropdown
                                        value={formData.gender}
                                        options={genderOptions}
                                        onChange={(e) => handleInputChange('gender', e.value)}
                                        disabled={!editing}
                                        className="w-full"
                                        placeholder="Seleccionar género"
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Dirección
                                    </label>
                                    <InputText
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        disabled={!editing}
                                        className="w-full"
                                        placeholder="Av. Principal 123, Distrito"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        Ciudad
                                    </label>
                                    <InputText
                                        value={formData.city}
                                        onChange={(e) => handleInputChange('city', e.target.value)}
                                        disabled={!editing}
                                        className="w-full"
                                        placeholder="Lima"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field mb-4">
                                    <label className="block text-900 font-medium mb-2">
                                        País
                                    </label>
                                    <InputText
                                        value={formData.country}
                                        onChange={(e) => handleInputChange('country', e.target.value)}
                                        disabled={!editing}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Divider />

                <div className="text-center">
                    <p className="text-600 mb-2">
                        Miembro desde: {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('es-PE') : 'N/A'}
                    </p>
                    <p className="text-600 m-0">
                        Última actualización: {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString('es-PE') : 'N/A'}
                    </p>
                </div>
            </Card>
        </div>
    );
};

export default ProfileInfo;
